import React from "react";

import PileStoryboard from "./PileStoryboard";
import StepStoryboard from "./StepStoryboard";

/**
 * LE BANDEAU DU BAS — un déroulé, parfois deux, jamais deux ouverts.
 *
 * LE PROBLÈME. Une page de module territoire est une SÉQUENCE DANS UNE
 * SÉQUENCE : elle occupe une place dans la pile de l'atelier, et elle contient
 * elle-même des étapes. Tant que le module s'éditait dans un écran séparé, les
 * deux déroulés ne se rencontraient jamais — on perdait simplement l'atelier de
 * vue en entrant dans le module. Maintenant qu'ils sont dans la même coque, ils
 * doivent cohabiter sans se disputer ni la hauteur ni le sens.
 *
 * LA RÉPONSE : DEUX LIGNES EMBOÎTÉES. Celle des étapes du module en premier,
 * celle de l'atelier dessous. Le déroulé d'étapes est indenté et marqué d'un
 * filet de couleur ; cette indentation n'est pas décorative, elle dit qu'il
 * APPARTIENT à la page que la ligne du dessous désigne. Deux bandeaux côte à
 * côte, ou deux barres de même rang, auraient posé la question « laquelle est
 * laquelle » à chaque coup d'œil.
 *
 * POURQUOI LES ÉTAPES AU-DESSUS DES PAGES, alors que la relation de contenance
 * irait plutôt dans l'autre sens. Parce que la proximité prime sur la
 * hiérarchie : la ligne des étapes commande ce qui est affiché juste au-dessus
 * d'elle — la carte, l'étape ouverte — et c'est celle qu'on manipule sans
 * arrêt. La mettre au contact de ce qu'elle pilote raccourcit le geste le plus
 * fréquent ; l'atelier, qu'on ne parcourt qu'en remontant d'un cran, ferme le
 * bandeau par le bas.
 *
 * UNE SEULE OUVERTE À LA FOIS. C'est ce qui fait tenir la construction : les
 * vignettes coûtent une centaine de pixels, et deux bandes ouvertes ne
 * laisseraient plus voir la carte qu'on est en train de cadrer. Déplier l'une
 * replie donc l'autre — et cette exclusion règle du même coup la question de
 * savoir laquelle on regarde, puisqu'il n'y en a jamais qu'une.
 *
 * Les deux fréquences d'usage sont d'ailleurs très différentes : on passe
 * d'étape en étape sans arrêt en composant un module, et de page en page
 * seulement quand on remonte au niveau de l'atelier. Ce sont deux gestes, pas
 * deux moitiés d'un même geste.
 */

const OPEN_KEY = "builder.storyboard.open";

/** `"none"`, `"pile"` ou `"steps"` — ce qui est déplié, mémorisé entre les sessions. */
export function readOpenPreference() {
  try {
    const stored = window.localStorage.getItem(OPEN_KEY);
    return ["none", "pile", "steps"].includes(stored) ? stored : "none";
  } catch {
    return "none";
  }
}

function writeOpenPreference(open) {
  try {
    window.localStorage.setItem(OPEN_KEY, open);
  } catch {
    /* mode privé, quota plein : le repli reste local à la session */
  }
}

export default function ComposerStoryboard({
  // Le déroulé de l'atelier
  pile, pagesById, activePageId, configName, onSelectPage, onAddPage,
  // Celui du module, quand la page ouverte en est un
  steps = null, activeStepIndex = 0, onSelectStep, onAddStep, onRemoveStep,
  open, onOpenChange,
}) {
  const hasSteps = Array.isArray(steps);
  const hasPile = pile.length > 0;
  if (!hasPile && !hasSteps) return null;

  const toggle = (which) => {
    const next = open === which ? "none" : which;
    writeOpenPreference(next);
    onOpenChange(next);
  };

  return (
    <footer className="composer-storyboard">
      {hasSteps && (
        // Le filet de couleur et le retrait sont posés par la feuille de style
        // sur `.composer-storyboard-nested` : c'est une relation de
        // CONTENANCE, elle se dessine autour de la ligne, pas dedans.
        <div className={`composer-storyboard-nested${hasPile ? "" : " is-alone"}`}>
          <StepStoryboard
            steps={steps}
            activeIndex={activeStepIndex}
            expanded={open === "steps"}
            onToggle={() => toggle("steps")}
            onSelect={onSelectStep}
            onAdd={onAddStep}
            onRemove={onRemoveStep}
          />
        </div>
      )}

      {hasPile && (
        <PileStoryboard
          pile={pile}
          pagesById={pagesById}
          activePageId={activePageId}
          configName={configName}
          expanded={open === "pile"}
          onToggle={() => toggle("pile")}
          onSelect={onSelectPage}
          onAdd={onAddPage}
        />
      )}
    </footer>
  );
}
